// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { Clickable, Icon, LoadingSpinner } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { NamedExoticComponent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    iconOnly?: boolean;
    sizeVariant?: string;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    ButtonPresentationProps;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export interface AddToCartButtonStyles {
    asButton?: ButtonPresentationProps;
}

export const productAddToCartButtonStyles: AddToCartButtonStyles = {
    asButton: {
        css: css`
            &&& {
                background-color: ${getColor("primary")} !important;
                color: ${getColor("common.white")};
                margin: 0;
                &:hover:not(:disabled) {
                    background-color: ${getColor("common.white")} !important;
                    border-color: ${getColor("primary")};
                    color: ${getColor("primary")};
                }
            }
            &.icon-only {
                // this is for button icon only
                display: flex;
                align-items: center;
                width: 54px;
                justify-content: center;
                padding: 0;

                svg {
                    color: ${getColor("common.white")};
                }

                &:hover:not(:disabled) {
                    svg {
                        color: ${getColor("primary")};
                        fill: ${getColor("primary")};
                    }
                }
            }

            &:not(.icon-only) {
                display: flex;
                align-items: center;
                justify-content: center;
                padding-left: 15px;
                padding-right: 15px;
                text-align: center;
                text-transform: uppercase;
                min-width: ${(props: any) => (props._sizeVariant === "medium" ? "156px" : "135px")};

                .atc-label {
                    font-size: ${fontConfig.fontSize.regular}px;
                    line-height: ${fontConfig.lineHeight.singleLine};
                    letter-spacing: 0.7px;

                    @media screen and (max-width: 359px) {
                        font-size: ${fontConfig.fontSize.small}px;
                    }
                }
            }

            &.loading {
                &,
                &:disabled {
                    &,
                    &:hover {
                        background: transparent !important;
                        border-color: transparent !important;
                        opacity: 1;
                    }
                }
            }
            &.success {
                &,
                &:disabled {
                    &,
                    &:hover {
                        opacity: 1;
                        border-color: ${getColor("primary")};
                    }
                }
            }

            &.default {
            }

            &:disabled {
                opacity: 0.5;
                &,
                &:hover {
                    background: ${getColor("primary")};
                    border-color: ${getColor("transparent")};
                }
            }
        `,
    },
};

interface AddToCartStatusInfoInterface {
    label: string;
    icon: NamedExoticComponent<{}> | string;
}

interface AddToCartStatusInterface {
    default: AddToCartStatusInfoInterface;
    loading: AddToCartStatusInfoInterface;
    success: AddToCartStatusInfoInterface;
}

const addToCartStatus: AddToCartStatusInterface = {
    default: {
        label: "Add to Cart",
        icon: "Cart",
    },
    loading: {
        label: "",
        icon: "",
    },
    success: {
        label: "",
        icon: "Check",
    },
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId },
        productInfo: { qtyOrdered, unitOfMeasure, failedToLoadInventory },
    },
    hasEnoughInventory,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    // most of our design is with icon
    iconOnly = true,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    sizeVariant,
    subscription,
    onAddToCartClick,
    ...otherProps
}: Props) => {
    const toasterContext: any = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [status, setStatus] = React.useState(addToCartStatus.default);

    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 3000);
        }
    }, [status]);

    const addToCartClickHandler = () => {
        onAddToCartClick?.();

        setStatus(addToCartStatus.loading);
        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
            onComplete: (cartline: any) => {
                // check if the type of result is a cartLine then show the toaster
                if (cartline && cartline?.hasOwnProperty("qtyOrdered")) {
                    setStatus(addToCartStatus.success);
                    if (showAddToCartConfirmationDialog) {
                        toasterContext.addToast({
                            body: (
                                <ProductAddedToCartMessage
                                    isQtyAdjusted={cartline.isQtyAdjusted}
                                    multipleProducts={false}
                                />
                            ),
                            messageType: "success",
                            timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                        });
                    }
                }
            },
        });
    };

    const isDisabled =
        qtyOrdered <= 0 ||
        status !== addToCartStatus.default ||
        (!hasEnoughInventory && !failedToLoadInventory) ||
        !canAddToCart ||
        isAddToCartButtonDisabled;

    if (iconOnly) {
        return (
            <Button
                aria-label="Add To Cart"
                {...styles.asButton}
                onClick={addToCartClickHandler}
                disabled={isDisabled}
                {...otherProps}
                className={
                    status === addToCartStatus.loading
                        ? "icon-only loading"
                        : status === addToCartStatus.success
                        ? "icon-only success"
                        : "icon-only default"
                }
            >
                {status === addToCartStatus.loading ? (
                    <LoadingSpinner size={sizeVariant === "small" ? 14 : 26} />
                ) : null}
                {status === addToCartStatus.success ? (
                    <Icon src="Check" size={sizeVariant === "small" ? 17 : 22} />
                ) : null}
                {status === addToCartStatus.default ? (
                    <Icon src={status.icon} size={sizeVariant === "small" ? 18 : 22} />
                ) : null}
            </Button>
        );
    }

    return (
        <Button
            aria-label="Add To Cart"
            {...styles.asButton}
            onClick={addToCartClickHandler}
            disabled={isDisabled}
            {...otherProps}
            className={
                status === addToCartStatus.loading
                    ? "loading"
                    : status === addToCartStatus.success
                    ? "success"
                    : "default"
            }
        >
            {status === addToCartStatus.loading ? (
                <Typography as="span" className="atc-icon atc-icon-loading">
                    <LoadingSpinner size={sizeVariant === "small" ? 22 : 30} color="primary" />
                </Typography>
            ) : null}
            {status === addToCartStatus.success ? (
                <Typography as="span" className="atc-label atc-label-success">
                    {translate("Added To Cart")}
                </Typography>
            ) : null}
            {status === addToCartStatus.default ? (
                <Typography className="atc-label" as="span">
                    {labelOverride ?? translate(status.label)}
                </Typography>
            ) : null}
        </Button>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
